import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../Auth/AuthContext';
import logger from '../../utils/logger';

// Hook de logique extrait de MailSettings.
export const useMailSettings = (backendUrl, authToken) => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('individual');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Visibilité des mots de passe
  const [showImapPassword, setShowImapPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  
  // Test de connexion
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Email Accounts State
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({
    email_address: '',
    display_name: '',
    account_type: 'individual',
    imap_host: '',
    imap_port: 993,
    imap_use_ssl: true,
    imap_username: '',
    imap_password: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_use_ssl: true,
    smtp_username: '',
    smtp_password: '',
    assigned_admin_id: '',
    authorized_admin_ids: [],
    email_signature: '',
    use_signature: true,
    is_active: true
  });
  
  // Admins List
  const [admins, setAdmins] = useState([]);
  
  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadEmailAccounts(),
        loadAdmins()
      ]);
    } catch (error) {
      logger.error('Error loading data:', error);
      showError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadEmailAccounts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/accounts`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmailAccounts(data);
      }
    } catch (error) {
      logger.error('Error loading email accounts:', error);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/admins`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      } else {
        // Si la requête échoue, au moins ajouter l'utilisateur connecté
        if (user && (user.role === 'admin' || user.role === 'super_admin')) {
          setAdmins([user]);
        }
      }
    } catch (error) {
      logger.error('Error loading admins:', error);
      // En cas d'erreur, au moins ajouter l'utilisateur connecté
      if (user && (user.role === 'admin' || user.role === 'super_admin')) {
        setAdmins([user]);
      }
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const testPayload = {
        imap_host: accountForm.imap_host,
        imap_port: accountForm.imap_port || 993,
        imap_use_ssl: accountForm.imap_use_ssl,
        imap_username: accountForm.imap_username,
        imap_password: accountForm.imap_password,
        smtp_host: accountForm.smtp_host,
        smtp_port: accountForm.smtp_port || 587,
        smtp_use_ssl: accountForm.smtp_use_ssl,
        smtp_username: accountForm.smtp_username,
        smtp_password: accountForm.smtp_password
      };
      
      // Ajouter l'account_id si on est en mode édition
      if (editingAccount) {
        testPayload.account_id = editingAccount.id;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/mail/test-connection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(testPayload)
      });


      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
      } else {
        const errorText = await response.text();
        console.error('❌ Erreur réponse:', response.status, errorText);
        throw new Error('Erreur lors du test de connexion');
      }
    } catch (error) {
      console.error('❌ Erreur complète:', error);
      logger.error('Error testing connection:', error);
      showError('Erreur lors du test de connexion');
    } finally {
      setTesting(false);
    }
  };

  const saveEmailAccount = async () => {
    setSaving(true);
    try {
      const url = editingAccount 
        ? `${backendUrl}/api/admin/mail/accounts/${editingAccount.id}`
        : `${backendUrl}/api/admin/mail/accounts`;
      
      const method = editingAccount ? 'PUT' : 'POST';
      
      // Préparer les données à envoyer
      let dataToSend = { 
        ...accountForm,
        imap_port: accountForm.imap_port || 993,
        smtp_port: accountForm.smtp_port || 587
      };
      
      // En mode édition, ne pas envoyer les passwords s'ils sont vides (inchangés)
      if (editingAccount) {
        if (!accountForm.imap_password || accountForm.imap_password.trim() === '') {
          delete dataToSend.imap_password;
        }
        if (!accountForm.smtp_password || accountForm.smtp_password.trim() === '') {
          delete dataToSend.smtp_password;
        }
      }
      
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });


      if (response.ok) {
        const result = await response.json();
        showSuccess(`Compte email ${editingAccount ? 'modifié' : 'créé'} avec succès`);
        await loadEmailAccounts();
        closeAccountModal();
      } else {
        const error = await response.json();
        console.error('❌ Erreur serveur:', error);
        throw new Error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('❌ Erreur complète:', error);
      logger.error('Error saving email account:', error);
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteEmailAccount = async (accountId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce compte email ?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/accounts/${accountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        showSuccess('Compte email supprimé avec succès');
        await loadEmailAccounts();
      } else {
        throw new Error('Erreur lors de la suppression');
      }
    } catch (error) {
      logger.error('Error deleting email account:', error);
      showError('Erreur lors de la suppression du compte');
    }
  };

  const setDefaultEmailAccount = async (accountId) => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/accounts/${accountId}/set-default`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });

      if (response.ok) {
        showSuccess('Email défini comme compte par défaut');
        await loadEmailAccounts();
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      logger.error('Error setting default email:', error);
      showError('Erreur lors de la définition par défaut');
    }
  };

  const openAccountModal = (account = null) => {
    if (account) {
      setEditingAccount(account);
      const formData = {
        email_address: account.email_address,
        display_name: account.display_name,
        account_type: account.account_type,
        imap_host: account.imap_host || '',
        imap_port: account.imap_port || 993,
        imap_use_ssl: account.imap_use_ssl !== undefined ? account.imap_use_ssl : true,
        imap_username: account.imap_username || '',
        imap_password: '',
        smtp_host: account.smtp_host || '',
        smtp_port: account.smtp_port || 587,
        smtp_use_ssl: account.smtp_use_ssl !== undefined ? account.smtp_use_ssl : true,
        smtp_username: account.smtp_username || '',
        smtp_password: '',
        assigned_admin_id: account.assigned_admin_id || '',
        authorized_admin_ids: account.authorized_admin_ids || [],
        email_signature: account.email_signature || '',
        use_signature: account.use_signature,
        is_active: account.is_active
      };
      setAccountForm(formData);
    } else {
      setEditingAccount(null);
      setAccountForm({
        email_address: '',
        display_name: '',
        account_type: 'individual',
        imap_host: '',
        imap_port: 993,
        imap_use_ssl: true,
        imap_username: '',
        imap_password: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_use_ssl: true,
        smtp_username: '',
        smtp_password: '',
        assigned_admin_id: '',
        authorized_admin_ids: [],
        email_signature: '',
        use_signature: true,
        is_active: true
      });
    }
    setShowAccountModal(true);
  };

  const closeAccountModal = () => {
    setShowAccountModal(false);
    setEditingAccount(null);
    setTestResult(null);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(''), 5000);
  };


  return {
    user,
    activeSection,
    setActiveSection,
    loading,
    setLoading,
    saving,
    setSaving,
    showImapPassword,
    setShowImapPassword,
    showSmtpPassword,
    setShowSmtpPassword,
    testing,
    setTesting,
    testResult,
    setTestResult,
    emailAccounts,
    setEmailAccounts,
    showAccountModal,
    setShowAccountModal,
    editingAccount,
    setEditingAccount,
    accountForm,
    setAccountForm,
    admins,
    setAdmins,
    successMessage,
    setSuccessMessage,
    errorMessage,
    setErrorMessage,
    loadInitialData,
    loadEmailAccounts,
    loadAdmins,
    testConnection,
    saveEmailAccount,
    deleteEmailAccount,
    setDefaultEmailAccount,
    openAccountModal,
    closeAccountModal,
    showSuccess,
    showError
  };
};
